/**
 * Formular zum Erstellen/Bearbeiten einer Betreuungsakte
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTabParams } from '@/hooks/useTabRoute';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';
import { WOHNFORM_OPTIONS } from '@/lib/wohnform';


const PFLEGEGRAD_OPTIONS = [
  { value: 'keiner', label: 'Kein Pflegegrad' },
  { value: '1', label: 'Pflegegrad 1' },
  { value: '2', label: 'Pflegegrad 2' },
  { value: '3', label: 'Pflegegrad 3' },
  { value: '4', label: 'Pflegegrad 4' },
  { value: '5', label: 'Pflegegrad 5' }
];

export default function KlientFormPage() {
  const navigate = useNavigate();
  const { id } = useTabParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    geburtsdatum: '',
    geburtsort: '',
    geschlecht: 'nicht_angegeben',
    familienstand: 'nicht_angegeben',
    adresse: '',
    plz: '',
    ort: '',
    telefon: '',
    email: '',
    fallnummer: '',
    status: 'aktiv',
    prioritaet: 'mittel',
    wohnform: 'nicht_angegeben',
    pflegegrad: 'keiner',
    schwerbehinderung: false,
    grad_der_behinderung: '',
    notizen: ''
  });

  useEffect(() => {
    if (isEditing) {
      loadAkte();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadAkte = async () => {
    setLoading(true);
    try {
      const data = await betreuungAPI.getKlient(id);
      setFormData({
        vorname: data.vorname || '',
        nachname: data.nachname || '',
        geburtsdatum: data.geburtsdatum || '',
        geburtsort: data.geburtsort || '',
        geschlecht: data.geschlecht || 'nicht_angegeben',
        familienstand: data.familienstand || 'nicht_angegeben',
        adresse: data.adresse || '',
        plz: data.plz || '',
        ort: data.ort || '',
        telefon: data.telefon || '',
        email: data.email || '',
        fallnummer: data.fallnummer || '',
        status: data.status || 'aktiv',
        prioritaet: data.prioritaet || 'mittel',
        wohnform: data.wohnform || 'nicht_angegeben',
        pflegegrad: data.pflegegrad || 'keiner',
        schwerbehinderung: data.schwerbehinderung || false,
        grad_der_behinderung: data.grad_der_behinderung || '',
        notizen: data.notizen || ''
      });
    } catch (error) {
      toast.error('Betreuungsakte nicht gefunden');
      navigate('/akten');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.vorname || !formData.nachname) {
      toast.error('Bitte Vor- und Nachname eingeben');
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await betreuungAPI.updateKlient(id, formData);
        toast.success('Betreuungsakte aktualisiert');
      } else {
        await betreuungAPI.createKlient(formData);
        toast.success('Betreuungsakte erstellt');
      }
      navigate('/akten');
    } catch (error) {
      toast.error(error.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="akte-form-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
        <h1 className="text-2xl font-bold text-slate-800">
          {isEditing ? 'Betreuungsakte bearbeiten' : 'Neue Betreuungsakte anlegen'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Stammdaten */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Stammdaten</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="vorname">Vorname *</Label>
              <Input
                id="vorname"
                value={formData.vorname}
                onChange={(e) => handleChange('vorname', e.target.value)}
                placeholder="Max"
                required
                data-testid="input-vorname"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nachname">Nachname *</Label>
              <Input
                id="nachname"
                value={formData.nachname}
                onChange={(e) => handleChange('nachname', e.target.value)}
                placeholder="Mustermann"
                required
                data-testid="input-nachname"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="geburtsdatum">Geburtsdatum</Label>
              <Input
                id="geburtsdatum"
                type="date"
                value={formData.geburtsdatum}
                onChange={(e) => handleChange('geburtsdatum', e.target.value)}
                data-testid="input-geburtsdatum"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="geburtsort">Geburtsort</Label>
              <Input
                id="geburtsort"
                value={formData.geburtsort}
                onChange={(e) => handleChange('geburtsort', e.target.value)}
                placeholder="Berlin"
                data-testid="input-geburtsort"
              />
            </div>
            <div className="space-y-2">
              <Label>Geschlecht</Label>
              <Select value={formData.geschlecht} onValueChange={(v) => handleChange('geschlecht', v)}>
                <SelectTrigger data-testid="select-geschlecht">
                  <SelectValue placeholder="Auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nicht_angegeben">Nicht angegeben</SelectItem>
                  <SelectItem value="maennlich">Männlich</SelectItem>
                  <SelectItem value="weiblich">Weiblich</SelectItem>
                  <SelectItem value="divers">Divers</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Familienstand</Label>
              <Select value={formData.familienstand} onValueChange={(v) => handleChange('familienstand', v)}>
                <SelectTrigger data-testid="select-familienstand">
                  <SelectValue placeholder="Auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nicht_angegeben">Nicht angegeben</SelectItem>
                  <SelectItem value="ledig">Ledig</SelectItem>
                  <SelectItem value="verheiratet">Verheiratet</SelectItem>
                  <SelectItem value="geschieden">Geschieden</SelectItem>
                  <SelectItem value="verwitwet">Verwitwet</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Aktenverwaltung */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Aktenverwaltung</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fallnummer">Aktenzeichen</Label>
              <Input
                id="fallnummer"
                value={formData.fallnummer}
                onChange={(e) => handleChange('fallnummer', e.target.value)}
                placeholder="z.B. XVII 123/24"
                data-testid="input-aktenzeichen"
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                <SelectTrigger data-testid="select-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="aktiv">Aktiv</SelectItem>
                  <SelectItem value="ruhend">Ruhend</SelectItem>
                  <SelectItem value="uebergabe">Übergabe</SelectItem>
                  <SelectItem value="beendet">Beendet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priorität</Label>
              <Select value={formData.prioritaet} onValueChange={(v) => handleChange('prioritaet', v)}>
                <SelectTrigger data-testid="select-prioritaet">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="niedrig">Niedrig</SelectItem>
                  <SelectItem value="mittel">Mittel</SelectItem>
                  <SelectItem value="hoch">Hoch</SelectItem>
                  <SelectItem value="dringend">Dringend</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Kontaktdaten */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Kontaktdaten</h2>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="adresse">Straße und Hausnummer</Label>
              <Input
                id="adresse"
                value={formData.adresse}
                onChange={(e) => handleChange('adresse', e.target.value)}
                placeholder="Musterstraße 1"
                data-testid="input-adresse"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="plz">PLZ</Label>
                <Input
                  id="plz"
                  value={formData.plz}
                  onChange={(e) => handleChange('plz', e.target.value)}
                  placeholder="12345"
                  data-testid="input-plz"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="ort">Ort</Label>
                <Input
                  id="ort"
                  value={formData.ort}
                  onChange={(e) => handleChange('ort', e.target.value)}
                  placeholder="Musterstadt"
                  data-testid="input-ort"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="telefon">Telefon</Label>
                <Input
                  id="telefon"
                  type="tel"
                  value={formData.telefon}
                  onChange={(e) => handleChange('telefon', e.target.value)}
                  placeholder="+49 123 456789"
                  data-testid="input-telefon"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="email@example.com"
                  data-testid="input-email"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Wohnsituation & Gesundheit */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Wohnsituation & Gesundheit</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Wohnform</Label>
              <Select value={formData.wohnform} onValueChange={(v) => handleChange('wohnform', v)}>
                <SelectTrigger data-testid="select-wohnform">
                  <SelectValue placeholder="Auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {WOHNFORM_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Pflegegrad</Label>
              <Select value={formData.pflegegrad} onValueChange={(v) => handleChange('pflegegrad', v)}>
                <SelectTrigger data-testid="select-pflegegrad">
                  <SelectValue placeholder="Auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {PFLEGEGRAD_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="schwerbehinderung">Schwerbehinderung</Label>
                <Switch
                  id="schwerbehinderung"
                  checked={formData.schwerbehinderung}
                  onCheckedChange={(v) => handleChange('schwerbehinderung', v)}
                  data-testid="switch-schwerbehinderung"
                />
              </div>
            </div>
            {formData.schwerbehinderung && (
              <div className="space-y-2">
                <Label htmlFor="gdb">Grad der Behinderung (GdB)</Label>
                <Input
                  id="gdb"
                  value={formData.grad_der_behinderung}
                  onChange={(e) => handleChange('grad_der_behinderung', e.target.value)}
                  placeholder="z.B. 50"
                  data-testid="input-gdb"
                />
              </div>
            )}
          </div>
        </div>

        {/* Notizen */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Notizen</h2>
          <Textarea
            value={formData.notizen}
            onChange={(e) => handleChange('notizen', e.target.value)}
            placeholder="Zusätzliche Notizen..."
            rows={4}
            data-testid="input-notizen"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={saving} data-testid="save-btn">
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isEditing ? 'Speichern' : 'Anlegen'}
          </Button>
        </div>
      </form>
    </div>
  );
}
